import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import SubHeader from '../../layout/components/SubHeader';
import Pagination from '../../common/Pagination';
import MediaListItem from './MediaListItem';
import MyFilesToolbar from './subparts/MyFilesToolbar';
import UploadMediaModal from './subparts/UploadMediaModal';
import MediaGridSkeleton from './subparts/MediaGridSkeleton';
import mediaService from '../../../services/media.service';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import './MyFiles.css';

const GRID_ROWS = 3;

function getGridColumns(width) {
    if (width >= 768) {
        return 4;
    }
    if (width >= 576) {
        return 3;
    }
    return 2;
}

function getPageSize(width) {
    return getGridColumns(width) * GRID_ROWS;
}

function isFileDragEvent(event) {
    return Array.from(event.dataTransfer?.types || []).includes('Files');
}

function sortMediaList(list, sortOption) {
    const result = [...list];

    switch (sortOption) {
        case 'name_asc':
            result.sort((a, b) => a.name.localeCompare(b.name));
            break;
        case 'name_desc':
            result.sort((a, b) => b.name.localeCompare(a.name));
            break;
        case 'createdAt_asc':
            result.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
            break;
        case 'createdAt_desc':
        default:
            result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            break;
    }

    return result;
}

function MyFiles() {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);

    const [mediaList, setMediaList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [pageNo, setPageNo] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOption, setSortOption] = useState('createdAt_desc');
    const [isPageDragging, setIsPageDragging] = useState(false);
    const [pageSize, setPageSize] = useState(() => getPageSize(window.innerWidth));

    const updateMediaList = useCallback(
        async (options = {}) => {
            const { silent = false } = options;

            if (!clinicId) {
                return;
            }

            if (!silent) {
                setLoading(true);
            }

            try {
                const res = await mediaService.getResources(clinicId);
                setMediaList(res.data || []);
            } catch {
                toast.error('Error loading media files');
            } finally {
                if (!silent) {
                    setLoading(false);
                }
            }
        },
        [clinicId]
    );

    useEffect(() => {
        updateMediaList();
    }, [updateMediaList]);

    useEffect(() => {
        setPageNo(0);
    }, [searchTerm, sortOption]);

    useEffect(() => {
        const handleResize = () => {
            setPageSize(getPageSize(window.innerWidth));
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        setPageNo(0);
    }, [pageSize]);

    const filteredMedia = useMemo(() => {
        let result = sortMediaList(mediaList, sortOption);

        if (searchTerm) {
            const query = searchTerm.toLowerCase();
            result = result.filter((item) => item.name.toLowerCase().includes(query));
        }

        return result;
    }, [mediaList, searchTerm, sortOption]);

    const totalPages = Math.max(1, Math.ceil(filteredMedia.length / pageSize));
    const currentItems = filteredMedia.slice(pageNo * pageSize, pageNo * pageSize + pageSize);

    const handleUpload = useCallback(
        (file) => {
            if (!file || !clinicId) {
                return;
            }

            setIsUploading(true);
            setUploadProgress(0);
            setIsPageDragging(false);

            const formData = new FormData();
            formData.append('file', file);

            mediaService
                .uploadMedia(clinicId, formData, (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    setUploadProgress(progress);
                })
                .then(() => {
                    updateMediaList({ silent: true });
                    setShowUploadModal(false);
                })
                .catch(() => {
                    toast.error('Upload failed');
                })
                .finally(() => {
                    setIsUploading(false);
                    setUploadProgress(0);
                });
        },
        [clinicId, updateMediaList]
    );

    useEffect(() => {
        const handleDragEnter = (event) => {
            if (isUploading || !isFileDragEvent(event)) {
                return;
            }

            event.preventDefault();
            setIsPageDragging(true);
            setShowUploadModal(true);
        };

        const handleDragOver = (event) => {
            if (!isFileDragEvent(event)) {
                return;
            }

            event.preventDefault();
            event.dataTransfer.dropEffect = 'copy';
        };

        const handleDrop = (event) => {
            if (!isFileDragEvent(event)) {
                return;
            }

            event.preventDefault();
            setIsPageDragging(false);

            if (isUploading || event.target.closest('.modal')) {
                return;
            }

            const file = event.dataTransfer.files?.[0];
            if (file) {
                setShowUploadModal(true);
                handleUpload(file);
            }
        };

        const handleDragEnd = () => {
            setIsPageDragging(false);
        };

        document.addEventListener('dragenter', handleDragEnter);
        document.addEventListener('dragover', handleDragOver);
        document.addEventListener('drop', handleDrop);
        document.addEventListener('dragend', handleDragEnd);

        return () => {
            document.removeEventListener('dragenter', handleDragEnter);
            document.removeEventListener('dragover', handleDragOver);
            document.removeEventListener('drop', handleDrop);
            document.removeEventListener('dragend', handleDragEnd);
        };
    }, [handleUpload, isUploading]);

    const handleRefresh = async () => {
        if (!clinicId || isRefreshing) {
            return;
        }

        setIsRefreshing(true);
        try {
            await updateMediaList({ silent: true });
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleCloseUploadModal = () => {
        if (!isUploading) {
            setShowUploadModal(false);
            setIsPageDragging(false);
        }
    };

    return (
        <div className="ecx-page my-files-page">
            {isPageDragging && !isUploading && (
                <div className="my-files-page-drop-overlay" aria-hidden="true">
                    <div className="my-files-page-drop-overlay__content">
                        <span className="my-files-page-drop-overlay__icon">
                            <i className="bi bi-cloud-arrow-up"></i>
                        </span>
                        <strong>Drop file to upload</strong>
                        <span className="small">Release anywhere on this page</span>
                    </div>
                </div>
            )}
            <SubHeader
                title="My Files"
                description={`Manage clinic images and media assets${
                    mediaList.length ? ` · ${mediaList.length} total` : ''
                }`}
                icon="bi-folder"
                containerClass="container-fluid px-4"
            >
                <button
                    type="button"
                    className="btn btn-light-primary btn-sm rounded-pill"
                    onClick={handleRefresh}
                    disabled={isRefreshing || loading}
                    title="Refresh files"
                >
                    <i className={`bi bi-arrow-clockwise me-1 ${isRefreshing ? 'ecx-refresh-spin' : ''}`}></i>
                    Refresh
                </button>
            </SubHeader>

            <div className="container-fluid px-4 py-3">
                <div className="ecx-page-card my-files-library">
                    <div className="ecx-page-panel__header">
                        <MyFilesToolbar
                            searchTerm={searchTerm}
                            onSearchTermChange={setSearchTerm}
                            sortOption={sortOption}
                            onSortChange={setSortOption}
                            totalElements={filteredMedia.length}
                            onUploadClick={() => setShowUploadModal(true)}
                        />
                    </div>

                    <div className="ecx-page-panel__body d-flex flex-column">
                        {loading ? (
                            <MediaGridSkeleton count={pageSize} />
                        ) : filteredMedia.length > 0 ? (
                            <>
                                <div className="row g-2 row-cols-2 row-cols-sm-3 row-cols-md-4 my-files-grid flex-grow-1">
                                    {currentItems.map((item) => (
                                        <div className="col" key={item.id}>
                                            <MediaListItem
                                                item={item}
                                                updateMediaList={() => updateMediaList({ silent: true })}
                                            />
                                        </div>
                                    ))}
                                </div>

                                {totalPages > 1 && (
                                    <div className="ecx-page-pagination border-top pt-3 mt-3">
                                        <Pagination
                                            currentPage={pageNo}
                                            totalPages={totalPages}
                                            totalElements={filteredMedia.length}
                                            pageSize={pageSize}
                                            onPageChange={setPageNo}
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="ecx-page-empty flex-grow-1">
                                <div className="ecx-page-empty__icon">
                                    <i className="bi bi-folder-x"></i>
                                </div>
                                <h5 className="text-dark fw-normal mb-2">
                                    {searchTerm ? 'No matching files found' : 'No media files yet'}
                                </h5>
                                <p className="mb-3">
                                    {searchTerm
                                        ? 'Try another search term or clear the filter.'
                                        : 'Upload your first file to get started.'}
                                </p>
                                {!searchTerm && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-accent"
                                        onClick={() => setShowUploadModal(true)}
                                    >
                                        <i className="bi bi-cloud-arrow-up me-1"></i>
                                        Upload Media
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <UploadMediaModal
                show={showUploadModal}
                onClose={handleCloseUploadModal}
                onUpload={handleUpload}
                isUploading={isUploading}
                uploadProgress={uploadProgress}
                isExternalDragging={isPageDragging}
            />
        </div>
    );
}

export default MyFiles;
